/**
 * Feedback sync: pure helpers for the local report queue and posting to the
 * collector. Kept free of React/store so the queue/flush logic is unit-tested.
 */
import type { FeedbackEntry } from './captureContext';
import { FEEDBACK_COLLECTOR_URL } from './collectorConfig';

/**
 * The endpoint to POST a report to. When a collector URL is configured (the
 * installed app reaching the PC over Tailscale) we post there; otherwise we fall
 * back to the same-origin /__feedback (the vite dev/preview server).
 */
export function feedbackEndpoint(collectorUrl: string = FEEDBACK_COLLECTOR_URL): string {
  const base = collectorUrl.trim();
  return base ? `${base.replace(/\/+$/, '')}/feedback` : '/__feedback';
}

/** Reports not yet confirmed synced to the collector (the flush queue). */
export function unsyncedEntries(
  entries: FeedbackEntry[],
  syncedIds: Iterable<string>,
): FeedbackEntry[] {
  const done = new Set(syncedIds);
  return entries.filter((e) => !done.has(e.id));
}

/** Add an id to the synced set without duplicating it. */
export function addSynced(syncedIds: string[], id: string): string[] {
  return syncedIds.includes(id) ? syncedIds : [...syncedIds, id];
}

/** How many of the given entries are synced / still queued. */
export function syncCounts(
  entries: FeedbackEntry[],
  syncedIds: Iterable<string>,
): { synced: number; unsynced: number } {
  const unsynced = unsyncedEntries(entries, syncedIds).length;
  return { synced: entries.length - unsynced, unsynced };
}

/**
 * POST one report (with optional screenshot) to the collector. Resolves true on a
 * 2xx, false on any failure — nothing throws, so a dead endpoint just leaves the
 * report queued. A timeout stops a hung request from blocking the flush.
 */
export async function postReport(
  entry: FeedbackEntry,
  screenshotDataUrl?: string,
  endpoint: string = feedbackEndpoint(),
  timeoutMs = 12_000,
): Promise<boolean> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(screenshotDataUrl ? { ...entry, screenshotDataUrl } : entry),
      signal: ctrl.signal,
    });
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}
